/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineTranslate, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useLanguageContext } from "../../context/LanguageContext";
import { pushOneLanguage } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Language = {
  language_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/language/add";
const editRoute = "/language/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    language,
    count,
    fetchLanguages,
    deleteLanguage,
    updateLanguageState,
  } = useLanguageContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!language) fetchLanguages(page, pageSize);
  }, [language]);

  const rows = useMemo<Language[] | null>(() => {
    if (!Array.isArray(language)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return language as Language[];
    return (language as Language[]).filter((l) =>
      l.name?.toString().toLowerCase().includes(q),
    );
  }, [language, query]);

  const columns: Column<Language>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineTranslate className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Language) => {
    const response: any = await deleteLanguage(row.language_id);
    if (response?.data?.status === 200 && Array.isArray(language)) {
      updateLanguageState(
        (language as Language[]).filter((l) => l.language_id !== row.language_id),
      );
    }
  };

  const rowActions = (row: Language): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneLanguage(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "The language will be removed from any record that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Language>
      title="Languages"
      subtitle="Browse, add and manage language reference data."
      icon={<HiOutlineTranslate className="h-5 w-5" />}
      actions={[{ label: "Add language", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.language_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search languages…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchLanguages(p, ps);
        },
      }}
      empty="No languages yet."
    />
  );
};

export default Overview;
